import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { fetchManualById } from '../../services/manuals'
import { fetchBibleChapter } from '../../services/bible'
import { cacheManual, getCachedManual } from '../../services/offlineCache'
import { getVerseDetails } from '../../utils/scripture-modal/bible-utils'
import { extractBibleChapterKeys } from '../../utils/manual-content-tag-utils'
import { IManual } from '../../utils/types'

/**
 * Fire-and-forget: pre-fetch and cache every unique Bible chapter referenced
 * across the manual's text[], body, summary, and memory_track fields.
 */
const preloadScriptures = (manual: IManual) => {
	const chapterKeys = new Map<string, { bookId: string; chapter: string }>()

	// From the heading text[] array (e.g. "John 3:16-18")
	for (const text of manual.text ?? []) {
		try {
			const { bookId, chapter } = getVerseDetails(text)
			chapterKeys.set(`${bookId}_${chapter}`, { bookId, chapter })
		} catch {
			// skip unparseable reference
		}
	}

	// From HTML fields that go through tagBibleVerses
	for (const html of [manual.body, manual.summary, manual.memory_track]) {
		if (!html) continue
		for (const key of extractBibleChapterKeys(html)) {
			chapterKeys.set(`${key.bookId}_${key.chapter}`, key)
		}
	}

	// Fetch each unique chapter — fetchBibleChapter is cache-first so no duplicate network calls
	Array.from(chapterKeys.values()).forEach(({ bookId, chapter }) => {
		fetchBibleChapter(bookId, chapter)
	})
}

const useManualData = () => {
	const [loading, setLoading] = useState(false)
	const [manualData, setManualData] = useState<IManual>()
	const { pathName } = useParams()

	useEffect(() => {
		const fetchData = async () => {
			setLoading(true)
			try {
				let custompathNameArray = pathName?.split('-')
				let id = custompathNameArray ? custompathNameArray[custompathNameArray.length - 1] : '14'

				const res = await fetchManualById(id)
				setManualData(res.data)
				localStorage.setItem('currentTheme', res.data.theme)

				// Cache manual for offline use
				cacheManual(id, res.data)

				// Preload all referenced Bible chapters in the background
				preloadScriptures(res.data)
			} catch (error) {
				// Network failed — serve from cache if available
				let custompathNameArray = pathName?.split('-')
				let id = custompathNameArray ? custompathNameArray[custompathNameArray.length - 1] : '14'
				const cached = await getCachedManual(id)
				if (cached) {
					setManualData(cached)
					localStorage.setItem('currentTheme', cached.theme)
				}
			} finally {
				setLoading(false)
			}
		}

		fetchData()
	}, [pathName])

	return { loading, manualData }
}

export default useManualData
